// ---------------------------------------------------------------------------
//  Shared ZIP utilities — extracted from close-account.js
// ---------------------------------------------------------------------------

// CRC-32 (ISO 3309 / ITU-T V.42)
const crc32Table = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[i] = c;
  }
  return t;
})();

export function crc32(data) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) crc = crc32Table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

// Deflate-raw via CompressionStream
export async function deflateRaw(data) {
  const cs = new CompressionStream('deflate-raw');
  const writer = cs.writable.getWriter();
  writer.write(data);
  writer.close();
  const chunks = [];
  const reader = cs.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const totalLen = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(totalLen);
  let pos = 0;
  for (const c of chunks) { out.set(c, pos); pos += c.length; }
  return out;
}

// DOS date/time for ZIP headers
export function dosDateTime() {
  const d = new Date();
  const time = ((d.getHours() & 0x1F) << 11)
             | ((d.getMinutes() & 0x3F) << 5)
             | ((d.getSeconds() >> 1) & 0x1F);
  const date = (((d.getFullYear() - 1980) & 0x7F) << 9)
             | (((d.getMonth() + 1) & 0x0F) << 5)
             | (d.getDate() & 0x1F);
  return { time, date };
}

// ---------------------------------------------------------------------------
//  Plain (unencrypted) deflate ZIP builder (in-memory fallback)
//  files: array of { name: string, data: Uint8Array }
//  onProgress: optional callback(currentIndex, total)
// ---------------------------------------------------------------------------
export async function buildPlainZip(files, onProgress) {
  const { time: dosTime, date: dosDate } = dosDateTime();

  const localParts = [];
  const centralParts = [];
  let offset = 0;

  for (let i = 0; i < files.length; i++) {
    const { name, data } = files[i];
    const nameBytes = new TextEncoder().encode(name);
    const crcVal = crc32(data);
    const deflated = await deflateRaw(data);

    if (onProgress) onProgress(i, files.length);

    // Local file header
    const localHdr = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(localHdr.buffer);
    lv.setUint32(0,  0x04034b50, true);        // local file header sig
    lv.setUint16(4,  20, true);                // version needed: 2.0
    lv.setUint16(6,  0, true);                 // general purpose flag
    lv.setUint16(8,  8, true);                 // compression method: deflate
    lv.setUint16(10, dosTime, true);
    lv.setUint16(12, dosDate, true);
    lv.setUint32(14, crcVal, true);
    lv.setUint32(18, deflated.length, true);   // compressed size
    lv.setUint32(22, data.length, true);       // uncompressed size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);                 // extra field length
    localHdr.set(nameBytes, 30);
    localParts.push(localHdr);
    localParts.push(deflated);

    // Central directory header
    const centHdr = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(centHdr.buffer);
    cv.setUint32(0,  0x02014b50, true);
    cv.setUint16(4,  20, true);                // version made by
    cv.setUint16(6,  20, true);                // version needed
    cv.setUint16(8,  0, true);                 // flags
    cv.setUint16(10, 8, true);                 // compression: deflate
    cv.setUint16(12, dosTime, true);
    cv.setUint16(14, dosDate, true);
    cv.setUint32(16, crcVal, true);
    cv.setUint32(20, deflated.length, true);
    cv.setUint32(24, data.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);                 // extra field length
    cv.setUint16(32, 0, true);                 // comment length
    cv.setUint16(34, 0, true);                 // disk number
    cv.setUint16(36, 0, true);                 // internal attrs
    cv.setUint32(38, 0, true);                 // external attrs
    cv.setUint32(42, offset, true);            // local header offset
    centHdr.set(nameBytes, 46);
    centralParts.push(centHdr);

    offset += localHdr.length + deflated.length;
  }

  if (onProgress) onProgress(files.length, files.length);

  const cdSize = centralParts.reduce((s, c) => s + c.length, 0);

  // End of Central Directory
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0,  0x06054b50, true);
  ev.setUint16(4,  0, true);
  ev.setUint16(6,  0, true);
  ev.setUint16(8,  files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);

  const totalSize = offset + cdSize + 22;
  const out = new Uint8Array(totalSize);
  let pos = 0;
  for (const p of localParts) { out.set(p, pos); pos += p.length; }
  for (const p of centralParts) { out.set(p, pos); pos += p.length; }
  out.set(eocd, pos);
  return out;
}

// ---------------------------------------------------------------------------
//  WinZip AE-2 helpers (relocated from close-account.js)
// ---------------------------------------------------------------------------

export const AE_EXTRA_LEN = 11;   // size of the 0x9901 extra field
export const AE_SALT_LEN  = 16;   // AES-256 salt
export const AE_PVV_LEN   = 2;    // password verification value
export const AE_AUTH_LEN  = 10;   // HMAC-SHA1 truncated
export const AE_OVERHEAD  = AE_SALT_LEN + AE_PVV_LEN + AE_AUTH_LEN;  // 28

export function writeAeExtra(buf, off) {
  const v = new DataView(buf.buffer, buf.byteOffset + off);
  v.setUint16(0, 0x9901, true);  // header ID
  v.setUint16(2, 7, true);       // data size
  v.setUint16(4, 0x0001, true);  // AE-1 (CRC stored for max compatibility)
  buf[off + 6] = 0x41;           // 'A'
  buf[off + 7] = 0x45;           // 'E'
  buf[off + 8] = 0x03;           // AES-256
  v.setUint16(9, 0x0008, true);  // actual compression = deflate
}

// PBKDF2-HMAC-SHA1 → 66 bytes (32 AES + 32 HMAC + 2 PVV) for AES-256
export async function aesDeriveKeys(password, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: 1000, hash: 'SHA-1' },
    keyMaterial,
    66 * 8,
  );
  const d = new Uint8Array(bits);
  return {
    aesKey:  d.slice(0, 32),
    hmacKey: d.slice(32, 64),
    pvv:     d.slice(64, 66),
  };
}

// AES-CTR with little-endian counter starting at 1 (WinZip variant).
export async function aesCtrEncrypt(rawKey, plaintext) {
  const key = await crypto.subtle.importKey(
    'raw', rawKey, { name: 'AES-CBC' }, false, ['encrypt'],
  );
  const zeroIv = new Uint8Array(16);
  const numBlocks = Math.ceil(plaintext.length / 16);
  const result = new Uint8Array(plaintext.length);

  const BATCH = 2048;
  for (let start = 0; start < numBlocks; start += BATCH) {
    const end = Math.min(start + BATCH, numBlocks);
    const promises = [];
    for (let i = start; i < end; i++) {
      const ctr = new Uint8Array(16);
      new DataView(ctr.buffer).setUint32(0, i + 1, true);
      promises.push(crypto.subtle.encrypt({ name: 'AES-CBC', iv: zeroIv }, key, ctr));
    }
    const blocks = await Promise.all(promises);
    for (let b = 0; b < blocks.length; b++) {
      const ks = new Uint8Array(blocks[b], 0, 16);
      const off = (start + b) * 16;
      const len = Math.min(16, plaintext.length - off);
      for (let j = 0; j < len; j++) result[off + j] = plaintext[off + j] ^ ks[j];
    }
  }
  return result;
}

// HMAC-SHA1 truncated to 10 bytes
export async function aesHmac(rawKey, data) {
  const key = await crypto.subtle.importKey(
    'raw', rawKey, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, data);
  return new Uint8Array(sig, 0, AE_AUTH_LEN);
}

// Deflate + encrypt one entry → salt(16) + pvv(2) + ciphertext + auth(10)
export async function aesEncryptEntry(password, plaintext) {
  const deflated = await deflateRaw(plaintext);
  const salt = crypto.getRandomValues(new Uint8Array(AE_SALT_LEN));
  const { aesKey, hmacKey, pvv } = await aesDeriveKeys(password, salt);
  const ciphertext = await aesCtrEncrypt(aesKey, deflated);
  const auth = await aesHmac(hmacKey, ciphertext);

  const out = new Uint8Array(AE_OVERHEAD + ciphertext.length);
  out.set(salt, 0);
  out.set(pvv, AE_SALT_LEN);
  out.set(ciphertext, AE_SALT_LEN + AE_PVV_LEN);
  out.set(auth, AE_SALT_LEN + AE_PVV_LEN + ciphertext.length);
  return out;
}

// ---------------------------------------------------------------------------
//  Streaming ZIP writer — plain (unencrypted) deflate
//  Writes local file headers + data immediately via writeFn,
//  buffers only central directory entries (~50 bytes each).
// ---------------------------------------------------------------------------
export class ZipStreamWriter {
  constructor(writeFn) {
    this._write = writeFn;
    this._centralEntries = [];
    this._offset = 0;
    this._count = 0;
    const { time, date } = dosDateTime();
    this._dosTime = time;
    this._dosDate = date;
  }

  async addEntry(name, data) {
    const nameBytes = new TextEncoder().encode(name);
    const crcVal = crc32(data);
    const deflated = await deflateRaw(data);

    // Local file header
    const localHdr = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(localHdr.buffer);
    lv.setUint32(0,  0x04034b50, true);
    lv.setUint16(4,  20, true);
    lv.setUint16(6,  0, true);
    lv.setUint16(8,  8, true);
    lv.setUint16(10, this._dosTime, true);
    lv.setUint16(12, this._dosDate, true);
    lv.setUint32(14, crcVal, true);
    lv.setUint32(18, deflated.length, true);
    lv.setUint32(22, data.length, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    localHdr.set(nameBytes, 30);

    await this._write(localHdr);
    await this._write(deflated);

    // Central directory entry (buffered)
    const centHdr = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(centHdr.buffer);
    cv.setUint32(0,  0x02014b50, true);
    cv.setUint16(4,  20, true);
    cv.setUint16(6,  20, true);
    cv.setUint16(8,  0, true);
    cv.setUint16(10, 8, true);
    cv.setUint16(12, this._dosTime, true);
    cv.setUint16(14, this._dosDate, true);
    cv.setUint32(16, crcVal, true);
    cv.setUint32(20, deflated.length, true);
    cv.setUint32(24, data.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, this._offset, true);
    centHdr.set(nameBytes, 46);
    this._centralEntries.push(centHdr);

    this._offset += localHdr.length + deflated.length;
    this._count++;
  }

  async finish() {
    let cdSize = 0;
    for (const entry of this._centralEntries) {
      await this._write(entry);
      cdSize += entry.length;
    }

    const eocd = new Uint8Array(22);
    const ev = new DataView(eocd.buffer);
    ev.setUint32(0,  0x06054b50, true);
    ev.setUint16(4,  0, true);
    ev.setUint16(6,  0, true);
    ev.setUint16(8,  this._count, true);
    ev.setUint16(10, this._count, true);
    ev.setUint32(12, cdSize, true);
    ev.setUint32(16, this._offset, true);
    ev.setUint16(20, 0, true);
    await this._write(eocd);
  }
}

// ---------------------------------------------------------------------------
//  Streaming ZIP writer — AES-256 encrypted (WinZip AE-2)
// ---------------------------------------------------------------------------
export class AesZipStreamWriter {
  constructor(writeFn, password) {
    this._write = writeFn;
    this._password = password;
    this._centralEntries = [];
    this._offset = 0;
    this._count = 0;
    const { time, date } = dosDateTime();
    this._dosTime = time;
    this._dosDate = date;
  }

  async addEntry(name, data) {
    const nameBytes = new TextEncoder().encode(name);
    const crcVal = crc32(data);
    const encData = await aesEncryptEntry(this._password, data);

    // Local file header
    const localHdr = new Uint8Array(30 + nameBytes.length + AE_EXTRA_LEN);
    const lv = new DataView(localHdr.buffer);
    lv.setUint32(0,  0x04034b50, true);
    lv.setUint16(4,  0x0033, true);          // version needed: 5.1
    lv.setUint16(6,  0x0001, true);          // flags: encrypted
    lv.setUint16(8,  99, true);              // compression method: AES
    lv.setUint16(10, this._dosTime, true);
    lv.setUint16(12, this._dosDate, true);
    lv.setUint32(14, crcVal, true);
    lv.setUint32(18, encData.length, true);
    lv.setUint32(22, data.length, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, AE_EXTRA_LEN, true);
    localHdr.set(nameBytes, 30);
    writeAeExtra(localHdr, 30 + nameBytes.length);

    await this._write(localHdr);
    await this._write(encData);

    // Central directory entry (buffered)
    const centHdr = new Uint8Array(46 + nameBytes.length + AE_EXTRA_LEN);
    const cv = new DataView(centHdr.buffer);
    cv.setUint32(0,  0x02014b50, true);
    cv.setUint16(4,  0x0033, true);
    cv.setUint16(6,  0x0033, true);
    cv.setUint16(8,  0x0001, true);
    cv.setUint16(10, 99, true);
    cv.setUint16(12, this._dosTime, true);
    cv.setUint16(14, this._dosDate, true);
    cv.setUint32(16, crcVal, true);
    cv.setUint32(20, encData.length, true);
    cv.setUint32(24, data.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, AE_EXTRA_LEN, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, this._offset, true);
    centHdr.set(nameBytes, 46);
    writeAeExtra(centHdr, 46 + nameBytes.length);
    this._centralEntries.push(centHdr);

    this._offset += localHdr.length + encData.length;
    this._count++;
  }

  async finish() {
    let cdSize = 0;
    for (const entry of this._centralEntries) {
      await this._write(entry);
      cdSize += entry.length;
    }

    const eocd = new Uint8Array(22);
    const ev = new DataView(eocd.buffer);
    ev.setUint32(0,  0x06054b50, true);
    ev.setUint16(4,  0, true);
    ev.setUint16(6,  0, true);
    ev.setUint16(8,  this._count, true);
    ev.setUint16(10, this._count, true);
    ev.setUint32(12, cdSize, true);
    ev.setUint32(16, this._offset, true);
    ev.setUint16(20, 0, true);
    await this._write(eocd);
  }
}
